import { useCallback, useEffect, useState } from "react";
import { trimTrailingSlash } from "../../lib/url";
import { promptToItem, PromptTechnique, ToolkitItem } from "./catalog";

// Kinds that are not directly runnable techniques (same set the old
// TechniquesTab rail excluded).
const EXCLUDED_KINDS = new Set(["summarize", "trend", "agent", "prepare"]);

export function usePromptTechniques(
  apiBaseUrl: string,
  ensureToken: () => Promise<string | null>
) {
  const API_BASE = trimTrailingSlash(apiBaseUrl || "");
  const [items, setItems] = useState<ToolkitItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        if (!API_BASE) throw new Error("Missing API base URL");
        const token = await ensureToken();
        if (!token) throw new Error("Not authenticated");
        const resp = await fetch(`${API_BASE}/prompts`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!resp.ok) {
          const text = await resp.text().catch(() => "");
          throw new Error(`GET /prompts failed ${resp.status} ${resp.statusText}${text ? ` – ${text}` : ""}`);
        }
        const data = JSON.parse((await resp.text()) || "{}");
        const arr: PromptTechnique[] = Array.isArray(data?.items)
          ? data.items
          : Array.isArray(data?.prompts)
          ? data.prompts
          : [];
        const runnable = arr.filter(
          (p) => !EXCLUDED_KINDS.has((p.kind || "").toLowerCase())
        );
        if (!cancelled) setItems(runnable.map(promptToItem));
      } catch (e: any) {
        if (!cancelled) setError(String(e?.message || e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  return { items, loading, error, reload };
}
